// MUST stay the first import: it arms the global crash handlers as a side
// effect, and ES modules evaluate imports in order — anything above it could
// throw while loading with nobody listening (KAN-398).
import '@/lib/telemetry/client-error-boot';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { htmlConfig } from '@/lib/html-config';
import { AppIntlProvider } from '@/lib/i18n/intl-provider';
import { resolveInitialLocale } from '@/lib/i18n/resolve-locale';
import { loadMessages } from '@/lib/i18n/messages';
import { initAppLock } from '@/lib/native/app-lock';
import { initNativeKeyboard } from '@/lib/native/keyboard';
import { initNativeUpdater } from '@/lib/native/updater';
import { initSessionRestore } from '@/lib/session-restore';
import { isNativeShell } from '@/lib/native/platform';
import { markOfficePane } from '@/lib/office/host';
import { initAssetCacheRefresh } from '@/lib/asset-cache-refresh';
import { initStaleBuildReload } from '@/lib/stale-build';
import { rememberComponentStack } from '@/lib/telemetry/client-error';
import { reportPendingReload } from '@/lib/telemetry/page-reload';
import { initAppHeight } from '@/lib/viewport-height';

import { RouteErrorComponent } from '@/components/route-error';

import { routeTree } from './routeTree.gen';

// Self-hosted Inter (replaces next/font). `--font-sans` in globals.css resolves
// to "Inter"; these weights cover the app's normal/medium/semibold/bold usage.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@/globals.css';

// The Office add-in (KAN-1766) carries its session as a Bearer token, like the
// app. Decided from the path the document was opened at, before the first
// request can go out (see lib/office/host.ts).
markOfficePane();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Scroll behaviour parity with the former app: restore on back/forward.
  scrollRestoration: true,
  // Friendly error boundary instead of the raw default error UI. Also handles
  // stale-deploy chunk-load errors (reload, deferred while a chat is streaming).
  defaultErrorComponent: RouteErrorComponent,
  // The only place the React component stack is available: the router hands
  // ``errorInfo`` to this hook and gives the error component a hard-coded empty
  // one. Park it for the crash report — with minified production stacks it is
  // the only readable part of a crash.
  defaultOnCatch: (error, errorInfo) => {
    rememberComponentStack(error, errorInfo?.componentStack ?? null);
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Resolve locale + load its message bundle BEFORE mount so the first paint is
// fully translated (parity with the former server-rendered messages).
const locale = resolveInitialLocale();
const messages = await loadMessages(locale);

document.documentElement.lang = locale;

// Track the visual viewport height in --app-height so keyboard-aware layouts
// (chat) stay pinned to the visible area instead of scrolling the header away.
// Covers mobile Safari; the native shell (resize:none) uses keyboard events.
initAppHeight();

// Tell the bootstrap self-heal in index.html to stand down: from here on the
// app's own handlers judge a failed asset load (verify the build, then reload
// or retry) instead of reloading the document on any /assets/ error.
window.__9brainsBooted = true;

// Log chunk preload failures without acting on them — the router retries
// speculative preloads, real navigation failures reach RouteErrorComponent.
initStaleBuildReload();

// Widen the resource-timing buffer while it is still empty: a cache repair
// (KAN-512) aims at the assets this document loaded last, and the default 250
// entries silently drop exactly those on a well-used tab.
initAssetCacheRefresh();

// If the previous load ended in an automatic reload, report why (KAN-196).
reportPendingReload();

// Native app: drive --app-height from the keyboard events (resize:none means
// the visual viewport doesn't shrink on iOS). The session token is NOT loaded
// here — the first native plugin call can stall while the bridge wires up, and
// awaiting it froze the app on a white screen. The /me bootstrap loads it
// lazily (post-mount, bridge ready) via ensureTokenLoaded() instead.
if (isNativeShell()) {
  // Marks the app for the touch-sized UI scale in globals.css. Set before
  // mount so the first frame is already at the right size — a resize after
  // paint would be visible as a jump.
  document.documentElement.dataset.nativeShell = 'true';
  void initNativeKeyboard();
  // Confirm the (possibly OTA-updated) bundle booted — without this the
  // Capgo updater rolls back to the previous bundle after ~10s.
  void initNativeUpdater();
  // Arm the biometric app lock BEFORE first paint so the gate covers the
  // very first frame on cold start (no content flash).
  initAppLock();
  // Take in a login that comes back as a deep link. Registered before the
  // first paint because the link may be what started the app: a login that
  // finished while the OS had reclaimed the webview arrives exactly this way,
  // and dropping it cost the user a second login (KAN-960).
  void import('@/lib/native/auth')
    .then((m) => m.initNativeLoginCallback())
    .catch((err) => console.error('[native-auth] callback listener failed:', err));
  // "Sprachmodus starten" from the Action Button, Siri or Spotlight (KAN-1907).
  // Before the first paint too: the action may be what started the app.
  void import('@/lib/native/quick-start')
    .then((m) => m.initQuickStart(() => void router.navigate({ to: '/chat/new' })))
    .catch((err) => console.error('[quick-start] listener failed:', err));
  // Remember the open chat and its unsent text whenever the app is
  // backgrounded, so an OS that reclaims the webview doesn't cost the user
  // their place and their half-typed message (KAN-547).
  initSessionRestore();
  // Build stamp in the native console (Xcode/logcat): pins down which web
  // bundle a device is actually running — stale ios/public syncs cost us a
  // debugging session on 2026-07-21.
  console.info('[build]', htmlConfig('build-id') ?? 'unstamped');
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppIntlProvider locale={locale} messages={messages}>
      <RouterProvider router={router} />
    </AppIntlProvider>
  </StrictMode>
);
